// Homepage feed — shows articles < 3 months old with category filter,
// plus a "Browse the Archive →" link that navigates to the dedicated /archive page.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";

const ALLOWED_CATEGORIES = ["AI & Books", "Books", "Education", "Learning & Memory", "Mindmaps"];

// Articles older than this threshold live only on /archive.
function getArchiveCutoffTs() {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - 3);
  return cutoff.getTime();
}

function parseDate(a) {
  const raw = a?.published_at || a?.publishedAt;
  if (!raw) return null;
  const ts = new Date(raw).getTime();
  return Number.isFinite(ts) ? ts : null;
}

export default function ArticlesGrid() {
  const [active, setActive] = useState("all");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  // null = not computed yet; until then all whitelisted tabs render (avoids a flash of missing tabs).
  const [recentCats, setRecentCats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles/categories")
      .then(({ data }) => {
        const cats = Array.isArray(data) ? data.filter((c) => ALLOWED_CATEGORIES.includes(c)) : [];
        setCategories(cats);
      })
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    // Tab visibility rule: the homepage only shows essays from the last 3 months,
    // so a whitelisted category with no RECENT essay gets its tab hidden here
    // (those essays remain reachable on /archive).
    api.get("/articles", { params: { limit: 500 } })
      .then(({ data }) => {
        const cutoff = getArchiveCutoffTs();
        const set = new Set();
        for (const a of Array.isArray(data) ? data : []) {
          const ts = parseDate(a);
          if ((ts === null || ts > cutoff) && a.category) set.add(a.category);
        }
        setRecentCats(set);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = active === "all" ? {} : { category: active };
    api.get("/articles", { params })
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [active]);

  const recent = useMemo(() => {
    const cutoff = getArchiveCutoffTs();
    return items.filter((a) => {
      const ts = parseDate(a);
      // Undated articles show as recent; anything on/before the cutoff moves to /archive.
      return ts === null || ts > cutoff;
    });
  }, [items]);

  const featured = recent[0];
  const rest = useMemo(() => recent.slice(1), [recent]);

  const visibleCats = recentCats ? categories.filter((c) => recentCats.has(c)) : categories;
  const tabs = [{ id: "all", label: "All articles" }, ...visibleCats.map((c) => ({ id: c, label: c }))];

  return (
    <section id="articles" className="py-20 sm:py-24 lg:py-32 border-t border-ink/5" data-testid="articles-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 sm:gap-6 mb-8 sm:mb-10">
          <div className="max-w-2xl">
            <div className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-brand mb-3">The journal</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-ink">
              Essays for readers who don't want to <span className="italic">forget what they read.</span>
            </h2>
          </div>
        </div>

        {tabs.length > 1 && (
          <div className="-mx-4 sm:mx-0 px-4 sm:px-0 flex items-center gap-2 overflow-x-auto pb-2 hide-scrollbar mb-8 sm:mb-10" data-testid="category-tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={`whitespace-nowrap px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm font-medium border transition-all ${
                  active === t.id
                    ? "bg-brand text-white border-brand shadow-sm"
                    : "bg-white text-ink-muted border-ink/10 hover:border-brand/40 hover:text-brand"
                }`}
                data-testid={`tab-${t.id.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7" data-testid="articles-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-ink/5 overflow-hidden">
                <div className="aspect-[16/10] bg-ink/5 animate-pulse" />
                <div className="p-6 space-y-3">
                  <div className="h-3 w-24 bg-ink/5 rounded animate-pulse" />
                  <div className="h-6 w-3/4 bg-ink/5 rounded animate-pulse" />
                  <div className="h-4 w-full bg-ink/5 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : recent.length === 0 ? (
          <div className="text-center py-20 text-ink-muted" data-testid="articles-empty">
            No recent essays in this category — try{" "}
            <Link to="/archive" className="underline text-brand">the archive</Link>.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7" data-testid="articles-grid">
              <ArticleCard article={featured} featured />
              {rest.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>

            <div className="mt-12 sm:mt-14 flex justify-center">
              <Link
                to="/archive"
                className="group inline-flex items-center gap-2 text-sm sm:text-base font-medium transition-colors"
                style={{ color: "#E8952A" }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "#c47a15"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "#E8952A"; }}
                data-testid="browse-archive-link"
              >
                Browse the Archive
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
